import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Paper,
    Divider,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useSessionClass } from '../../contexts/SessionClassContext';
import { useStudents } from '../../hooks/useStudents';
import { useClasses } from '../../hooks/useClasses';
import { useTeacher } from '../../hooks/useTeacher';
import { useAttendance } from '../../hooks/useAttendance';
import { useAuth } from '../../hooks/useAuth';
import { useTenantHeader } from '../../hooks/useTenantHeader';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useHolidays } from '../../hooks/useHolidays';
import { useError } from '../../contexts/ErrorContext';
import { format, parseISO } from 'date-fns';

interface AttendanceManagementModalProps {
    open: boolean;
    onClose: () => void;
    initialDate?: string;
}

const AttendanceManagementModal: React.FC<AttendanceManagementModalProps> = ({ open, onClose, initialDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showError } = useError();
    const schoolId = user?.schoolId;
    const { selectedSession, selectedClass: contextSelectedClass } = useSessionClass();

    const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
    const [attendanceDate, setAttendanceDate] = useState<string>('');
    const [selectedTeacherId, setSelectedTeacherId] = useState<number | ''>('');
    const [studentAttendance, setStudentAttendance] = useState<Record<number, string>>({});
    const [showTeacherError, setShowTeacherError] = useState(false);
    const [lastNotifiedSelection, setLastNotifiedSelection] = useState<string>('');

    const { tenantLoading } = useTenantHeader();
    const { classesQuery } = useClasses(schoolId ?? undefined);
    const { teachersQuery } = useTeacher(schoolId ?? undefined);
    const { useStudentsBySessionIdAndClassId } = useStudents(tenantLoading);
    const { bulkAttendanceMutation, attendanceStatusQuery, useAttendanceByDateQuery } = useAttendance();
    const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);

    // Format date for the attendance check API (dd-MM-yyyy)
    const formattedAttendanceCheckDate = useMemo(() => {
        if (!attendanceDate) return '';
        try {
            return format(parseISO(attendanceDate), "dd-MM-yyyy");
        } catch (e) {
            return '';
        }
    }, [attendanceDate]);

    // Query to check if attendance exists for the selected date
    const attendanceCheckQuery = useAttendanceByDateQuery(
        Number(selectedClassId) || undefined,
        selectedSession?.id,
        formattedAttendanceCheckDate || undefined
    );

    // Watch for existing attendance and show notification
    useEffect(() => {
        const selectionKey = `${selectedClassId}-${attendanceDate}`;
        if (open && !initialDate && attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0) {
            // Only show the error once for this selection
            if (lastNotifiedSelection !== selectionKey) {
                showError(`${t('attendance_already_exist_for_this_date')}`);
                setLastNotifiedSelection(selectionKey);
            }
        } else if (open && !initialDate && attendanceCheckQuery.data?.length === 0) {
            // If data becomes empty (date switched), we can clear the notified state
            setLastNotifiedSelection('');
        }
    }, [attendanceCheckQuery.data, open, initialDate, t, showError, selectedClassId, attendanceDate, lastNotifiedSelection]);

    const isDateDisabled = (date: Date) => {
        if (!date || isNaN(date.getTime())) return false;
        if (!useholidaysQueryBySession.data) return false;
        const dateStrForHoliday = format(date, "dd-MM-yyyy");
        return useholidaysQueryBySession.data.some((holiday) => {
            const isDateMatch = holiday.dates.includes(dateStrForHoliday);
            if (!isDateMatch) return false;

            if (holiday.forAllClasses) return true;
            return holiday.classIds?.includes(Number(selectedClassId) || 0);
        });
    };

    const studentsBySessionClassQuery = useStudentsBySessionIdAndClassId(
        selectedSession?.id,
        selectedClassId || undefined
    );

    // Initialize from context or initialDate
    useEffect(() => {
        if (open) {
            if (contextSelectedClass?.id) {
                setSelectedClassId(contextSelectedClass.id);
            }
            if (initialDate) {
                setAttendanceDate(initialDate);
            } else {
                setAttendanceDate('');
                // Default teacher selection based on logged-in user's userId
                if (user && teachersQuery.data) {
                    const currentTeacher = teachersQuery.data.find(t => t.userId === user.id);
                    if (currentTeacher) {
                        setSelectedTeacherId(currentTeacher.id);
                    } else {
                        setSelectedTeacherId('');
                    }
                } else {
                    setSelectedTeacherId('');
                }
                setLastNotifiedSelection('');
                setShowTeacherError(false);
            }
        }
    }, [open, contextSelectedClass, initialDate, user, teachersQuery.data]);

    // Fetch existing attendance for the selected date and class
    const { getAttendanceQuery: existingAttendanceQuery } = useAttendance({
        sessionId: selectedSession?.id || 0,
        classId: Number(selectedClassId) || 0,
        startDate: attendanceDate,
        endDate: attendanceDate
    });

    // Fetch all marked attendance dates for the current class and session to disable them in the calendar
    const { getAttendanceQuery: allMarkedAttendanceQuery } = useAttendance({
        sessionId: selectedSession?.id || 0,
        classId: Number(selectedClassId) || 0,
        startDate: selectedSession?.startDate || undefined,
        endDate: selectedSession?.endDate || undefined
    });

    const markedDates = useMemo(() => {
        if (!allMarkedAttendanceQuery.data) return new Set<string>();
        const dates = new Set<string>();
        allMarkedAttendanceQuery.data.forEach(record => {
            dates.add(record.attendanceDate);
        });
        return dates;
    }, [allMarkedAttendanceQuery.data]);

    // Populate studentAttendance when existing data is fetched
    useEffect(() => {
        if (open) {
            // Priority: attendanceCheckQuery.data (if date changed or newly opened)
            // fallback to existingAttendanceQuery.data (for initialDate/edit mode)
            const attendanceData = (attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0)
                ? attendanceCheckQuery.data
                : (initialDate ? existingAttendanceQuery.data : null);

            if (attendanceData && attendanceDate) {
                const attendanceMap: Record<number, string> = {};
                attendanceData.forEach(record => {
                    attendanceMap[record.studentId] = record.statusCode;
                });
                setStudentAttendance(attendanceMap);

                // Also set the teacher from existing attendance if available
                if (initialDate && attendanceData.length > 0) {
                    const firstRecord = attendanceData[0];
                    const teacherId = firstRecord.staffId || firstRecord.teacherId;
                    if (teacherId) {
                        setSelectedTeacherId(Number(teacherId));
                    }
                }
            } else if (!initialDate) {
                // Only clear if not in edit mode
                setStudentAttendance({});
            }
        }
    }, [attendanceCheckQuery.data, existingAttendanceQuery.data, open, initialDate, attendanceDate]);

    // Filter students by class and session
    const filteredStudents = useMemo(() => {
        if (!studentsBySessionClassQuery.data) return [];
        return studentsBySessionClassQuery.data.filter(
            (s) => s.status !== false
        ).sort((a, b) => (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()));
    }, [studentsBySessionClassQuery.data]);

    const handleStatusChange = (studentId: number, status: string) => {
        setStudentAttendance((prev) => {
            if (prev[studentId] === status) {
                const newState = { ...prev };
                delete newState[studentId];
                return newState;
            }
            return {
                ...prev,
                [studentId]: status,
            };
        });
    };


    const handleSubmit = () => {
        if (!attendanceDate) {
            showError("Please select a date.");
            return;
        }

        if (!selectedTeacherId) {
            setShowTeacherError(true);
            return;
        }

        const studentIds = Object.keys(studentAttendance).map(Number);
        if (!selectedClassId || !selectedSession?.id) return;

        // Check if all students have their attendance marked
        if (filteredStudents.length > 0 && studentIds.length < filteredStudents.length) {
            showError("Please Mark Attendance of all students.");
            return;
        }

        const students = studentIds.map(id => {
            const statusCode = studentAttendance[id];
            const status = attendanceStatusQuery.data?.find(s => s.statusCode.toUpperCase() === statusCode.toUpperCase());
            return {
                studentId: id,
                statusId: status?.id || 0,
                remarks: statusCode
            };
        });

        bulkAttendanceMutation.mutate({
            staffId: Number(selectedTeacherId),
            classId: Number(selectedClassId),
            sessionId: selectedSession.id,
            attendanceDate: attendanceDate, // API expects yyyy-MM-dd
            students: students
        }, {
            onSuccess: () => {
                onClose();
            },
            onError: (error: any) => {
                const apiMessage = error?.response?.data?.message;
                if (apiMessage === "One or more attendance statuses not found for provided ids") {
                    showError("PLease Mark Attendance of all students.");
                } else {
                    showError(apiMessage || t('failed_to_submit_attendance'));
                }
            }
        });
    };

    return (
        <Dialog open={open} onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                onClose();
            }
        }} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">{t('attendance_management')}</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
                <Box sx={{ p: 3, pb: 0 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label={t('session')}
                                value={selectedSession?.name || ''}
                                InputProps={{ readOnly: true }}
                                variant="outlined"
                                size="small"
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label={t('class')}
                                value={contextSelectedClass ? `${contextSelectedClass.name} - ${contextSelectedClass.section}` : ''}
                                InputProps={{ readOnly: true }}
                                variant="outlined"
                                size="small"
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('date')}
                                    value={attendanceDate ? parseISO(attendanceDate) : null}
                                    format="dd-MM-yyyy"
                                    onChange={(date: Date | null) => {
                                        if (date && !isNaN(date.getTime())) {
                                            setAttendanceDate(format(date, "yyyy-MM-dd"));
                                        } else {
                                            setAttendanceDate('');
                                        }
                                    }}
                                    shouldDisableDate={isDateDisabled}
                                    disabled={!!initialDate}
                                    slotProps={{
                                        textField: { fullWidth: true, size: "small" },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small" error={showTeacherError}>
                                <InputLabel id="teacher-select-label">{t('teacher_name')}</InputLabel>
                                <Select
                                    labelId="teacher-select-label"
                                    id="teacher-select"
                                    value={selectedTeacherId}
                                    label={t("teacher_name")}
                                    onChange={(e) => {
                                        setSelectedTeacherId(e.target.value as number);
                                        setShowTeacherError(false);
                                    }}
                                    disabled={teachersQuery.isLoading}
                                >
                                    {/* Only include current user as an fallback option if no teacher record matches their userId */}
                                    {user && !teachersQuery.data?.some(t => t.userId === user.id) && (
                                        <MenuItem key={`user-${user.id}`} value={user.id}>
                                            {user.username}
                                        </MenuItem>
                                    )}
                                    {teachersQuery.data?.map((teacher) => (
                                        <MenuItem key={teacher.id} value={teacher.id}>
                                            {teacher.firstName} {teacher.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {showTeacherError && <FormHelperText>{t('required')}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 2 }}>
                    {filteredStudents.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                            <Typography variant="body1" color="textSecondary">
                                {selectedClassId ? t('no_students_found') : t('please_select_a_class')}
                            </Typography>
                        </Box>
                    ) : (
                        filteredStudents.map((student) => (
                            <Paper
                                key={student.id}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    mb: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    gap: 2,
                                    transition: 'background-color 0.2s',
                                    '&:hover': { bgcolor: '#f9f9f9' }
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {student.studentName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('roll_number')}: {student.rollNumber || 'N/A'} | {t('enrolment_no')}: {student.enrolmentNo}
                                    </Typography>
                                </Box>

                                <Box display="flex" gap={1}>
                                    <Button
                                        variant={studentAttendance[student.id!] === 'PRESENT' ? 'contained' : 'outlined'}
                                        color="success"
                                        size="small"
                                        onClick={() => handleStatusChange(student.id!, 'PRESENT')}
                                        sx={{ borderRadius: 2, textTransform: 'uppercase', px: 2, minWidth: '80px', fontWeight: 'bold' }}
                                    >
                                        {t('present')}
                                    </Button>
                                    <Button
                                        variant={studentAttendance[student.id!] === 'ABSENT' ? 'contained' : 'outlined'}
                                        color="error"
                                        size="small"
                                        onClick={() => handleStatusChange(student.id!, 'ABSENT')}
                                        sx={{ borderRadius: 2, textTransform: 'uppercase', px: 2, minWidth: '80px', fontWeight: 'bold' }}
                                    >
                                        {t('absent')}
                                    </Button>
                                    <Button
                                        variant={studentAttendance[student.id!] === 'LEAVE' ? 'contained' : 'outlined'}
                                        color="warning"
                                        size="small"
                                        onClick={() => handleStatusChange(student.id!, 'LEAVE')}
                                        sx={{ borderRadius: 2, textTransform: 'uppercase', px: 2, minWidth: '80px', fontWeight: 'bold' }}
                                    >
                                        {t('leave')}
                                    </Button>
                                </Box>
                            </Paper>
                        ))
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Button onClick={onClose} variant="outlined" color="inherit">{t('cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={bulkAttendanceMutation.isLoading || !attendanceDate || (!initialDate && (attendanceCheckQuery.data && attendanceCheckQuery.data.length > 0))}
                    sx={{
                        bgcolor: '#3092e3',
                        '&:hover': { bgcolor: '#1976d2' },
                        px: 4,
                        fontWeight: 'bold'
                    }}
                >
                    {bulkAttendanceMutation.isLoading ? t('submitting...') : t('submit_attendance')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AttendanceManagementModal;
